import React from 'react';
import { SectionHeader } from '../../molecules/sections/SectionHeader';
import './sectionDetailsPane.module.scss';
import { SectionBody } from '../../molecules/sections/SectionBody';
import { Spinner } from '../../../../commonv2/components/Spinner';
import { BlockPane } from '../../molecules/blocks/BlockPane';
import { observable, makeObservable } from 'mobx';
import { SectionDetailsPaneState } from './states/SectionDetailsPaneState';
import { inject, observer } from 'mobx-react';
import { CourseShowPageState } from '../../../containers/courses/states/CourseShowPageState';
import { NotFoundPage } from '../../../../mainv2/containers/common/NotFoundPage';
import { UnderstandingCheck } from '../../molecules/sections/UnderstandingCheck';
import { GlobalStatesV3 } from '../../../../commonv3/states/GlobalStatesV3';
import { BackButton } from '../../../../commonv3/components/atoms/buttons/BackButton';
import { V3Routes } from '../../../routes';
import { CompleteCheck } from '../../molecules/sections/CompleteCheck';
import { LoaderRow } from '../../atoms/layout/LoaderRow';
import { HomeworkSubmissionBanner } from '../../molecules/lesson_homeworks/HomeworkSubmissionBanner';
import { CourseCategoryShowView } from '../../../types/courseCategories';
import { RemoteResourceV3 } from '../../../../commonv3/api/RemoteResourceV3';
import { SectionShowView } from '../../../types/sections';
import { CoachSkillCertificationState } from '../../../states/CoachSkillCertificationState';
import { CoachSkillType } from '../../../types/coachSkillCertifications';

type BlockListProps = {
  sectionDetail: RemoteResourceV3<SectionShowView>;
  courseCategory?: CourseCategoryShowView;
  currentUserSkillTypes: CoachSkillType[];
};
const BlockList = ({ sectionDetail, courseCategory, currentUserSkillTypes }: BlockListProps) => {
  if (sectionDetail.loading) {
    return <Spinner />;
  }
  if (!sectionDetail.get()) {
    throw new Error('Unable to retrieve sectionDetail');
  }
  return (
    <>
      {sectionDetail.get().blocks.map((block, index) => (
        <BlockPane
          key={block.id}
          index={index}
          block={block}
          courseCategory={courseCategory}
          currentUserSkillTypes={currentUserSkillTypes}
        />
      ))}
    </>
  );
};

type Props = {
  courseState: CourseShowPageState;
  coachSkillState: CoachSkillCertificationState;
  match: {
    params: {
      sectionId: string;
      lessonId: string;
    };
  };
};

export const SectionDetailsPane = inject(
  'courseState',
  'coachSkillState'
)(
  observer(
    class SectionDetailsPane extends React.Component<Props> {
      pageState: SectionDetailsPaneState = null;
      constructor(props: Props) {
        super(props);

        makeObservable(this, {
          pageState: observable,
        });

        this.pageState = new SectionDetailsPaneState(props.courseState);
      }

      componentDidMount(): void {
        this.init();
      }

      componentDidUpdate(prevProps: Readonly<Props>): void {
        if (prevProps.match.params.sectionId !== this.props.match.params.sectionId) {
          this.init();
        }
      }

      init() {
        const params = this.props.match.params;
        const lessonId = Number(params.lessonId);
        const sectionId = Number(params.sectionId);
        this.pageState.init(lessonId, sectionId);
      }

      render() {
        const {
          lessonId,
          sectionId,
          sectionDetail: sectionDetailResource,
          lesson,
          section,
          courseState,
        } = this.pageState;
        const currentUserSkillTypes = this.props.coachSkillState.certificates.getOrElse([]).map((c) => c.type);
        if (sectionDetailResource.loading || courseState.loading) {
          return (
            <div styleName="pane">
              <LoaderRow />
            </div>
          );
        }
        if (sectionDetailResource.isEmpty() || !lesson || !section) {
          return <NotFoundPage />;
        }
        const sectionDetail = sectionDetailResource.get();
        if (!lesson.activated) {
          GlobalStatesV3.routing.push(V3Routes.course(courseState.courseId));
        }
        const nextSection = courseState.getNextSection(lessonId, sectionId);
        return (
          <div styleName="pane" id="section-detail-pane">
            {GlobalStatesV3.viewport.isMobile && (
              <BackButton onClick={() => GlobalStatesV3.routing.push(V3Routes.course(courseState.courseId))} />
            )}
            <SectionHeader lesson={lesson} section={section} />
            <SectionBody>
              {sectionDetail.linked_homework && <HomeworkSubmissionBanner homework={sectionDetail.linked_homework} />}
              <BlockList
                sectionDetail={sectionDetailResource}
                courseCategory={courseState.course.value?.course_category}
                currentUserSkillTypes={currentUserSkillTypes}
              />
              {nextSection && (
                <UnderstandingCheck
                  lessonIndex={nextSection[0].index}
                  sectionIndex={nextSection[1].index}
                  sectionTitle={nextSection[1].title}
                  onNextClick={() => courseState.handleSectionCompleteButtonClick(lessonId, sectionId)}
                />
              )}
              {!nextSection && (
                <CompleteCheck
                  onCompleteClick={() => courseState.handleSectionCompleteButtonClick(lessonId, sectionId)}
                />
              )}
            </SectionBody>
          </div>
        );
      }
    }
  )
);
